/** Phase 8 (finance & analytics) panel strings — English. Spread into `admin.en.ts` (ADR-801). */
export const adminFaz8En = {
  reports: {
    navGroup: 'Reports and purchasing',
    period: 'Period',
    presets: {
      this_month: 'This month',
      last_month: 'Last month',
      last_7: 'Last 7 days',
      last_30: 'Last 30 days',
      last_90: 'Last 90 days',
      this_year: 'This year',
      last_year: 'Last year',
      custom: 'Custom range',
    },
    from: 'From',
    to: 'To',
    interval: 'Group by',
    intervals: { day: 'Day', week: 'Week', month: 'Month', year: 'Year' },
    all: 'All',
    csv: 'Download CSV',
    xlsx: 'Download Excel',
    print: 'Print / PDF',
    printedAt: 'Printed: {{date}}',
    vsNamed: '{{name}}: {{value}}',
    vsPrevious: 'Previous period: {{value}}',
    deltaUp: '{{pct}}% up',
    deltaDown: '{{pct}}% down',
    deltaFlat: 'No change',
    deltaNew: 'None in previous period',
    partial: 'The period is not over yet; today’s figures may still change.',
    currencyNote:
      'Amounts in TRY. Foreign-currency orders are converted at the CBRT forex buying rate at order time; returns and cancellations count in the period the order was placed.',
    empty: 'No data for this period.',
    none: 'Unassigned',
    loadingAgain: 'Updating…',
    chartEmpty: 'No sales to chart.',
    tooltip: '{{bucket}}: {{value}}',
    dashboard: {
      noPermission: 'Sales figures require the sales reports permission.',
      tilesLabel: 'Sales summary',
      today: 'Net sales today',
      todayOrders: 'Orders today',
      yesterday: 'Yesterday',
      last30: 'Net sales, last 30 days',
      last30Orders: 'Orders, last 30 days',
      chartTitle: 'Last 30 days: daily net sales (incl. VAT)',
      open: 'Open sales report',
    },
    share: 'Share',
  },
  salesReport: {
    tilesTitle: 'Period summary',
    navLabel: 'Sales report',
    store: 'Store',
    channel: 'Channel',
    segment: 'Customer type',
    channels: { web: 'Web store', b2b: 'Dealer portal', manual: 'Panel order', api: 'API' },
    segments: { retail: 'Retail', dealer: 'Dealer' },
    tiles: {
      netSales: 'Net sales',
      orders: 'Orders',
      averageOrder: 'Average order',
      deductions: 'Returns and cancellations',
      newCustomers: 'New customers',
    },
    chartTitle: 'Net sales (incl. VAT)',
    summaryTitle: 'Gross to net',
    rows: {
      productGross: 'Gross product sales (before discounts)',
      discounts: 'Discounts',
      shipping: 'Shipping and COD fees',
      orderTotal: 'Order value',
      cancellations: 'Cancelled amount',
      refunds: 'Refunded amount',
      deductions: 'Returns and cancellations',
      netSales: 'Net sales (incl. VAT)',
      tax: 'VAT',
      netSalesExVat: 'Net sales (excl. VAT)',
      itemsSold: 'Items sold',
      cancelledOrders: 'Fully cancelled orders',
      customers: 'Ordering customers',
      returningCustomers: 'Returning customers',
      newCustomerNetSales: 'Net sales from new customers',
      returningCustomerNetSales: 'Net sales from returning customers',
    },
    thisPeriod: 'This period',
    previousPeriod: 'Previous period',
    deductionNote:
      'Returns and cancellations is the larger of the cancelled and refunded amounts: a paid order must be refunded before it is cancelled, so the two are not added.',
    seriesTitle: 'Period table',
    columns: {
      bucket: 'Period start',
      orders: 'Orders',
      orderTotal: 'Order value',
      deductions: 'Returns & cancellations',
      netSales: 'Net sales',
      netSalesExVat: 'Excl. VAT',
      averageOrder: 'Average order',
      name: 'Name',
      quantity: 'Quantity',
    },
    total: 'Total',
    awaiting: '{{count}} orders ({{amount}}) awaiting payment; they count once paid.',
    fxMissing: 'No exchange rate for {{count}} foreign-currency orders; they are excluded.',
    breakdownTitle: 'Breakdown',
    breakdown: 'Break down by',
    breakdowns: {
      product: 'Product',
      variant: 'Variant (SKU)',
      category: 'Category',
      brand: 'Brand',
      country: 'Country',
      channel: 'Channel',
      store: 'Store',
      segment: 'Customer type',
      paymentMethod: 'Payment method',
    },
    categoryNote:
      'A product in several categories counts in each; category totals can exceed the overall total. Share is of the overall total.',
    lineNote:
      'Product figures use VAT-inclusive line amounts, minus cancelled units and reduced by the order’s refund ratio; shipping is excluded.',
    showing: 'Showing {{shown}} of {{total}} rows; the file has all of them.',
  },
  financeInputs: {
    navLabel: 'Finance inputs',
    section: 'Section',
    sections: {
      fees: 'Payment fees',
      shipments: 'Shipping costs',
      shippingRules: 'Shipping cost estimates',
      expenses: 'Business expenses',
    },
    readOnly: 'You can view these figures; changing them needs the finance inputs permission.',
    providers: {
      iyzico: 'iyzico (card)',
      paytr: 'PayTR (card)',
      bank_transfer: 'Bank transfer',
      cash_on_delivery: 'Cash on delivery',
      open_account: 'Open account',
      manual: 'Manual (transfer/cash etc.)',
    },
    provider: 'Payment method',
    installments: 'Installments',
    allInstallments: 'All other installments',
    installmentN: '{{n}} installments',
    singlePayment: 'Single payment',
    rate: 'Rate (%)',
    fixedFee: 'Fixed fee per transaction',
    validFrom: 'Valid from',
    validTo: 'Valid until',
    openEnded: 'Open-ended',
    note: 'Note',
    addFeeRule: 'Add fee rule',
    editFeeRule: 'Edit fee rule',
    saveFeeRule: 'Save rule',
    feeRuleSaved: 'Fee rule saved.',
    feeRuleDeleted: 'Fee rule deleted.',
    confirmDeleteFee: 'Delete this fee rule? Calculated fees in the finance report will change.',
    emptyFees: 'No fee rules',
    emptyFeesLead:
      'Enter the rates in your payment provider contract. Until then fees show as "no data" in the finance report.',
    rateInvalid: 'Enter a rate between 0 and 50 (e.g. 2.49).',
    amountInvalid: 'Enter an amount (e.g. 125.50).',
    filter: 'Show',
    filters: { all: 'All shipments', missing: 'Cost not entered', entered: 'Cost entered' },
    carrier: 'Carrier',
    allCarriers: 'All carriers',
    shipmentsSummary:
      '{{shipments}} shipments · entered (incl. VAT): {{entered}} ({{enteredCount}} shipments) · estimated (excl. VAT): {{estimated}} · unknown: {{unknown}}',
    columns: {
      date: 'Date',
      order: 'Order',
      carrier: 'Carrier',
      tracking: 'Tracking no',
      weight: 'Weight',
      cost: 'Cost',
      source: 'Source',
      provider: 'Payment method',
      installments: 'Installments',
      rate: 'Rate',
      fixedFee: 'Fixed fee',
      validity: 'Validity',
      maxWeight: 'Max weight',
      category: 'Category',
      description: 'Description',
      amount: 'Amount (incl. VAT)',
      vat: 'VAT',
      reference: 'Document no',
      store: 'Store',
    },
    estimated: 'estimated, excl. VAT',
    unknownCost: 'unknown',
    sources: { manual: 'Manual', import: 'File' },
    enterCost: 'Enter cost',
    editCost: 'Edit shipping cost',
    newCost: 'Shipping cost: {{tracking}}',
    amount: 'Amount (incl. VAT)',
    vat: 'VAT amount',
    reference: 'Invoice / document no',
    entryDate: 'Document date',
    saveCost: 'Save cost',
    costSaved: 'Shipping cost saved.',
    costDeleted: 'Shipping cost deleted.',
    confirmDeleteCost: 'Delete the shipping cost of this shipment?',
    emptyShipments: 'No shipments in this period',
    import: 'Upload carrier invoice',
    importTitle: 'Carrier invoice file',
    importLead:
      'CSV or Excel: "Tracking no" and "Amount" columns are required; "VAT", "Invoice no" and "Date" are optional. A preview comes first; costs are written when you confirm.',
    file: 'File',
    preview: 'Preview',
    apply: 'Save matched rows',
    overwrite: 'Replace existing costs with the file amounts',
    importSummary:
      '{{ready}} rows ready · {{exists}} shipments already have a cost · {{errors}} rows with problems',
    importApplied: 'Shipping cost saved for {{count}} shipments.',
    rowStatus: { ready: 'Ready', exists: 'Already entered', error: 'Problem' },
    row: 'Row',
    status: 'Status',
    problem: 'Details',
    addRule: 'Add estimate rule',
    editRule: 'Edit estimate rule',
    saveRule: 'Save rule',
    ruleSaved: 'Estimate rule saved.',
    ruleDeleted: 'Estimate rule deleted.',
    confirmDeleteRule: 'Delete this estimate rule?',
    maxWeight: 'Max weight per package (kg)',
    noLimit: 'No limit',
    cost: 'Cost per shipment (excl. VAT)',
    emptyRules: 'No estimate rules',
    emptyRulesLead: 'Enter your carrier price list; shipments without an entered cost are then estimated.',
    kg: '{{kg}} kg',
    category: 'Category',
    allCategories: 'All categories',
    categories: {
      rent: 'Rent',
      salary: 'Salaries and staff',
      marketing: 'Marketing (non-ads)',
      software: 'Software and subscriptions',
      packaging: 'Packaging',
      logistics: 'Warehouse and logistics',
      utilities: 'Utilities',
      professional: 'Accounting, legal, consulting',
      tax_fees: 'Taxes and fees',
      other: 'Other',
    },
    expenseDate: 'Date',
    description: 'Description',
    store: 'Store',
    noStore: 'Whole business',
    addExpense: 'Add expense',
    editExpense: 'Edit expense',
    saveExpense: 'Save expense',
    expenseSaved: 'Expense saved.',
    expenseDeleted: 'Expense deleted.',
    confirmDeleteExpense: 'Delete this expense?',
    emptyExpenses: 'No expenses in this period',
    emptyExpensesLead:
      'Enter rent, salaries, software and similar costs; they feed net profit and cash flow. Ad spend comes from the ads center.',
    expensesTotal: 'Total: {{amount}} (VAT {{vat}})',
  },
  financeReport: {
    navLabel: 'Finance report',
    tiles: {
      revenue: 'Revenue (excl. VAT)',
      grossProfit: 'Gross profit',
      contribution: 'Contribution margin',
      operatingProfit: 'Operating profit',
      netCash: 'Net cash flow',
    },
    marginOf: 'Of revenue: {{pct}}',
    grossUnknown: 'Some sales have no cost; gross profit may look higher than it is.',
    pnlTitle: 'Profitability (order basis, excl. VAT)',
    rows: {
      netSales: 'Net sales (incl. VAT)',
      revenue: 'Revenue (net sales, excl. VAT)',
      cogs: 'Cost of goods sold (COGS)',
      grossProfit: 'Gross profit',
      shipping: 'Shipping cost',
      fees: 'Payment fees',
      ads: 'Ad spend',
      disputes: 'Payment dispute losses and fees',
      contribution: 'Contribution margin',
      expenses: 'Operating expenses',
      operatingProfit: 'Operating profit',
    },
    statuses: { complete: 'Complete', partial: 'Partial', missing: 'No data' },
    notes: {
      cogsUnknown:
        'Cost unknown for {{amount}} of sales; gross profit looks higher than it is. Gross margin on lines with known cost: {{pct}}.',
      cogsUnknownAll: 'Cost of {{amount}} of sales is unknown; gross profit looks higher than it is.',
      cogsLate: '{{amount}} of cost was captured later (estimated).',
      shippingEstimated: '{{amount}} calculated from estimate rules.',
      shippingUnknown: 'Cost unknown for {{count}} shipments (not entered, no estimate rule).',
      feesUnknown: 'No fee rule for {{count}} card payments.',
      adsMissing: 'No ad account connected; ad spend counted as zero.',
      adsFx: 'No exchange rate for some days of foreign-currency ad spend.',
      disputes:
        '{{count}} lost or accepted disputes: {{lost}} (incl. VAT; goods assumed not returned) and dispute fees {{fees}}.',
    },
    goInputs: 'Go to finance inputs',
    pnlCols: { item: 'Item', amount: 'Amount', share: 'Of revenue', status: 'Data status' },
    expenseOf: 'Expense: {{name}}',
    cashFx: '{{count}} foreign-currency payments without an exchange rate were left out of cash flow.',
    dealerCollectionsHidden:
      'Dealer collections are business-wide; hidden with a store, channel or retail filter and for store-scoped users.',
    inByMethod: 'Payments received: {{name}}',
    noData: 'No data to show for this period.',
    noProducts: 'No products sold in this period.',
    showingProducts: 'Showing the top {{count}} products by revenue.',
    cashTitle: 'Cash flow (by transaction date)',
    cashRows: {
      in: 'Cash in',
      dealerCollections: 'Dealer collections (ledger)',
      out: 'Cash out',
      refunds: 'Refunds',
      fees: 'Payment fees',
      shipping: 'Carrier payments (incl. VAT)',
      expenses: 'Operating expenses (incl. VAT)',
      ads: 'Ad spend',
      suppliers: 'Supplier payments',
      disputes: 'Payment disputes (lost amount and fees)',
      net: 'Net cash flow',
    },
    cashNote:
      'Approximate management view, not a bank reconciliation. Fees are calculated from your fee rules.',
    vatTitle: 'VAT summary',
    vatRows: {
      output: 'VAT on sales',
      inputExpenses: 'VAT paid on expenses',
      inputShipping: 'VAT paid on shipping',
      inputPurchases: 'VAT on supplier invoices (purchases)',
      difference: 'Difference',
    },
    vatNote:
      'VAT on purchases comes from supplier invoices posted at goods receipt. Not a tax return — confirm with your accountant.',
    balancesTitle: 'Receivables and payables (today)',
    balanceRows: {
      dealerReceivables: 'Dealer receivables ({{count}} dealers owe)',
      openDisputes: 'Open payment disputes — amount at risk ({{count}} disputes)',
      codInTransit: 'Cash on delivery in transit ({{count}} orders)',
      supplierPayables: 'Supplier payables ({{count}} suppliers · overdue {{overdue}})',
    },
    dealerScopeHidden: 'Not shown: your access is limited to some stores',
    openDisputesLink: 'Open disputes',
    feesTitle: 'Payment fees by method',
    feeCols: {
      provider: 'Payment method',
      processed: 'Processed',
      fee: 'Fee',
      noRule: 'Payments without a rule',
    },
    productsTitle: 'Product profitability',
    productCols: {
      product: 'Product',
      revenue: 'Revenue (excl. VAT)',
      cogs: 'COGS',
      grossProfit: 'Gross profit',
      margin: 'Margin',
    },
    unknownCost: 'cost unknown',
    estimatedCost: 'estimated cost',
    channelsTitle: 'Channel profitability',
    channelCols: {
      channel: 'Channel',
      revenue: 'Revenue',
      cogs: 'COGS',
      shipping: 'Shipping',
      fees: 'Fees',
      contribution: 'Contribution (excl. ads)',
    },
    reconTitle: 'Payment reconciliation',
    reconCols: { provider: 'Provider', status: 'Last run', finished: 'Date', open: 'Open differences' },
    reconStatus: { running: 'Running', ok: 'Matched', mismatch: 'Differences', error: 'Error' },
    reconEmpty: 'No reconciliation run yet.',
    openPayments: 'Go to payments',
    missingTitle: 'Products sold without a cost',
    missingLead:
      'Products sold in this period without a cost (best sellers first). Enter the cost on the product; later orders use it, earlier ones are captured as "estimated".',
    missingCols: { product: 'Product', sku: 'SKU', quantity: 'Units sold' },
    missingNone: 'Every product sold in this period has a cost.',
    coverage: 'Cost at sale: {{snapshot}} lines · captured later: {{late}} · unknown: {{missing}}',
  },
  stockReport: {
    navLabel: 'Stock report',
    warehouse: 'Warehouse',
    allWarehouses: 'All warehouses',
    slowDays: 'Slow-moving threshold',
    slowDaysOption: 'No sale in {{days}} days',
    tilesTitle: 'Stock summary',
    tiles: {
      value: 'Stock value',
      units: 'Units on hand',
      turnover: 'Turnover (period)',
      daysOfInventory: 'Days of inventory',
      slow: 'Slow-moving stock',
    },
    unitsSub: '{{count}} variants in stock',
    valueSub: 'At current cost, excl. VAT',
    unknownCostSub: '{{count}} units have no cost',
    turnoverValue: '{{x}} times',
    turnoverSub: '{{sold}} units sold · average stock {{avg}} units',
    daysValue: '{{days}} days',
    noSales: 'No sales in period',
    slowSub: '{{count}} variants · {{units}} units',
    costHidden:
      'You cannot view costs, so stock value and cost columns are hidden; units and turnover are complete.',
    method:
      'Stock value = units on hand × current product cost (excl. VAT; foreign-currency cost at the latest CBRT rate). The product cost is updated as a moving weighted average at goods receipt.',
    negative: '{{count}} units are negative (sold before being received); not valued.',
    fxMissing: 'No exchange rate for the cost currency of {{count}} variants; not valued.',
    alertsTitle: 'Stock needing attention',
    alerts: {
      expired: 'Expired',
      expiring: 'Expiring within {{days}} days',
      critical: 'At or below critical level',
    },
    alertUnits: '{{units}} units',
    alertVariants: '{{count}} variants',
    goStock: 'Open in Stock',
    warehousesTitle: 'Stock by warehouse',
    warehouseCols: {
      warehouse: 'Warehouse',
      variants: 'Variants',
      units: 'Units on hand',
      value: 'Stock value',
      unknown: 'Units without cost',
    },
    inactive: 'inactive',
    categoriesTitle: 'Stock by category',
    categoryNote: 'Products in several categories count in each; rows may add up to more than the total.',
    categoryCols: {
      category: 'Category',
      units: 'Units on hand',
      value: 'Stock value',
      sold: 'Sold in period',
    },
    variantsTitle: 'Stock and turnover by variant',
    slowTitle: 'Slow-moving products',
    slowLead: 'Variants with stock and no sale in the last {{days}} days (by stock value).',
    slowNone: 'No slow-moving products.',
    variantCols: {
      product: 'Product',
      onHand: 'On hand',
      available: 'Available',
      unitCost: 'Unit cost',
      value: 'Stock value',
      sold: 'Sold in period',
      turnover: 'Turnover',
      cover: 'Cover',
      lastSale: 'Last sale',
    },
    noCost: 'no cost',
    uncategorized: 'Uncategorized',
    never: 'Never sold',
    showing: 'Showing the top {{shown}} of {{total}} variants by value; the download has all of them.',
    empty: 'No products in stock.',
  },
  purchasing: {
    navSuppliers: 'Suppliers',
    navOrders: 'Purchasing',
    navReceive: 'Goods receipt',
    statuses: {
      requested: 'Request',
      ordered: 'Ordered',
      partially_received: 'Partly received',
      received: 'Fully received',
      closed: 'Closed',
      cancelled: 'Cancelled',
    },
    filters: { all: 'All orders', open: 'Open orders' },
    supplier: 'Supplier',
    supplierStatus: { active: 'Active', inactive: 'Inactive' },
    addSupplier: 'Add supplier',
    editSupplier: 'Edit supplier',
    saveSupplier: 'Save supplier',
    supplierSaved: 'Supplier saved.',
    fields: {
      name: 'Legal name',
      code: 'Code',
      taxOffice: 'Tax office',
      taxNumber: 'Tax / ID number',
      contactName: 'Contact person',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      currency: 'Currency',
      paymentTermDays: 'Payment terms (days)',
      leadTimeDays: 'Lead time (days)',
      note: 'Note',
      status: 'Status',
    },
    supplierCols: {
      supplier: 'Supplier',
      contact: 'Contact',
      terms: 'Terms',
      balance: 'Balance',
      overdue: 'Overdue',
      status: 'Status',
    },
    noSuppliers: 'No suppliers yet',
    noSuppliersLead:
      'Add the companies you buy from; purchase orders and the supplier ledger belong to a supplier.',
    termsDays: '{{days}} days',
    cash: 'Cash',
    performanceTitle: 'Supplier performance',
    performanceLead:
      'Based on orders placed in the period; spend from goods received in the period (excl. VAT).',
    perfCols: {
      supplier: 'Supplier',
      orders: 'Orders',
      onTime: 'On time',
      fill: 'Fill rate',
      short: 'Short lines',
      over: 'Over lines',
      lead: 'Avg. lead time',
      spend: 'Spend',
    },
    late: '{{count}} late',
    ledgerTitle: 'Ledger',
    ledgerLead:
      'Positive amounts are owed to the supplier; negative are payments or credits. Receipt invoices are posted automatically.',
    addEntry: 'Add ledger entry',
    saveEntry: 'Save entry',
    entrySaved: 'Ledger entry added.',
    reverse: 'Reverse',
    reverseTitle: 'Correct with a reversing entry',
    reverseReason: 'Reason',
    reverseSave: 'Create reversal',
    reversed: 'Reversing entry created.',
    kinds: {
      opening: 'Opening balance',
      invoice: 'Invoice (goods receipt)',
      payment: 'Payment',
      return: 'Return / credit',
      adjustment: 'Adjustment',
      reversal: 'Reversal',
    },
    directions: { debit: 'Increase debt', credit: 'Decrease debt' },
    methods: {
      bank_transfer: 'Bank transfer',
      cash: 'Cash',
      cheque: 'Cheque',
      note: 'Promissory note',
      card: 'Card',
      other: 'Other',
    },
    entryFields: {
      kind: 'Entry type',
      amount: 'Amount',
      direction: 'Direction',
      date: 'Date',
      dueDate: 'Due date',
      method: 'Payment method',
      reference: 'Receipt / document no',
      description: 'Description',
    },
    ledgerCols: {
      date: 'Date',
      kind: 'Entry',
      description: 'Description',
      due: 'Due',
      amount: 'Amount',
      balance: 'Balance',
    },
    noEntries: 'No ledger entries.',
    balanceLine: 'Balance: {{amount}} · overdue: {{overdue}}',
    reversedMark: 'reversed',
    ordersOfSupplier: 'Orders from this supplier',
    newOrder: 'New purchase request',
    noOrders: 'No purchase orders',
    noOrdersLead:
      'Create a request; “Place order” marks it as sent to the supplier, and goods are received when they arrive.',
    orderCols: {
      number: 'No',
      supplier: 'Supplier',
      warehouse: 'Warehouse',
      expected: 'Expected',
      status: 'Status',
      progress: 'Received',
      total: 'Total (incl. VAT)',
    },
    progress: '{{received}} / {{ordered}}',
    warehouse: 'Receiving warehouse',
    expectedDate: 'Expected delivery date',
    note: 'Note',
    linesTitle: 'Products',
    addProduct: 'Add product',
    lineCols: {
      product: 'Product',
      quantity: 'Qty',
      received: 'Received',
      remaining: 'Remaining',
      unitCost: 'Unit cost (excl. VAT)',
      tax: 'VAT %',
      total: 'Amount (excl. VAT)',
    },
    taxDefault: 'Product VAT rate',
    removeLine: 'Remove line',
    totals: { subtotal: 'Subtotal (excl. VAT)', tax: 'VAT', total: 'Total (incl. VAT)' },
    createOrder: 'Create request',
    saveOrder: 'Save request',
    orderCreated: 'Purchase request created.',
    orderSaved: 'Saved.',
    editLines: 'Edit request',
    placeOrder: 'Place order',
    placeConfirm:
      'The request will be marked as ordered from the supplier; lines can no longer change. Continue?',
    placed: 'Marked as ordered.',
    cancelOrder: 'Cancel order',
    cancelConfirm: 'The order will be cancelled. Only orders with no receipts can be cancelled.',
    cancelled: 'Order cancelled.',
    closeOrder: 'Close order',
    closeConfirm: 'Remaining quantities will not be expected; the order will be closed.',
    closed: 'Order closed.',
    print: 'Print',
    orderInfo: 'Order details',
    requestedOn: 'Requested on',
    orderedOn: 'Ordered on',
    noExpected: 'Not set',
    changeDate: 'Change date',
    saveDate: 'Save date',
    receiptsTitle: 'Goods receipts',
    noReceipts: 'No goods received yet.',
    receiptLine: '{{number}} · {{date}} · {{count}} lines',
    invoiceNo: 'Invoice / delivery note no',
    lot: 'Lot {{lot}}',
    expiry: 'Expiry {{date}}',
    pickSupplier: 'Choose a supplier',
    pickWarehouse: 'Choose a warehouse',
    quantityInvalid: 'Quantity must be a whole number of 1 or more.',
    costInvalid: 'Amount could not be read (e.g. 120.50).',
    receiveTitle: 'Goods receipt: {{number}}',
    receive: 'Receive goods',
    receiveSave: 'Save receipt',
    received: 'Receipt saved; stock updated.',
    receiveLead:
      'Enter the quantities that arrived; leave missing lines at 0. For lot-tracked products enter the lot number and expiry date.',
    receivedOn: 'Received on',
    receiveCols: {
      product: 'Product',
      remaining: 'Remaining',
      quantity: 'Received qty',
      lot: 'Lot no',
      expiry: 'Expiry',
      cost: 'Invoice unit cost',
    },
    acceptOver: 'Accept more than the remaining quantity (over-delivery)',
    postInvoice: 'Post invoice to supplier ledger (incl. VAT, with terms)',
    updateCost: 'Update product cost (moving weighted average)',
    nothingToReceive: 'Enter a quantity on at least one line.',
    receivableEmpty: 'No orders awaiting receipt',
    receivableLead: 'Orders placed with suppliers and not fully received. Costs are not shown here.',
    receivableCols: {
      number: 'Order',
      supplier: 'Supplier',
      warehouse: 'Warehouse',
      expected: 'Expected',
      lines: 'Remaining',
    },
    remainingLines: '{{count}} lines · {{units}} units',
    overdueMark: 'late',
    costUpdated: 'Products with updated cost: {{count}}',
  },
  reportBuilder: {
    navLabel: 'Report builder',
    datasets: { orders: 'Orders', lines: 'Product sales', stock: 'Stock (today)' },
    datasetLeads: {
      orders: 'Counted orders as in the sales report; amounts in TRY, by order date.',
      lines: 'Lines of counted orders; excl. VAT, refunds prorated; COGS at sale.',
      stock: 'Current stock balance; value at current cost.',
    },
    metrics: {
      orders: 'Orders',
      cancelledOrders: 'Cancelled orders',
      grossSales: 'Order total',
      deductions: 'Returns and cancellations',
      netSales: 'Net sales',
      netSalesExVat: 'Net sales (excl. VAT)',
      shipping: 'Shipping',
      items: 'Units sold',
      newCustomers: 'New customers',
      averageOrder: 'Average order',
      quantity: 'Quantity',
      cogs: 'COGS',
      grossProfit: 'Gross profit',
      unknownCostSales: 'Sales with unknown cost',
      marginBps: 'Gross margin',
      onHand: 'On hand',
      reserved: 'Reserved',
      available: 'Available',
      stockValue: 'Stock value',
      unknownCostUnits: 'Units without cost',
    },
    dims: {
      day: 'Day',
      week: 'Week',
      month: 'Month',
      year: 'Year',
      store: 'Store',
      channel: 'Channel',
      segment: 'Customer type',
      paymentMethod: 'Payment method',
      country: 'Country',
      city: 'City',
      customerType: 'New / returning',
      product: 'Product',
      variant: 'SKU',
      category: 'Category',
      brand: 'Brand',
      warehouse: 'Warehouse',
      expiryMonth: 'Expiry month',
    },
    customerTypes: { new: 'New customer', returning: 'Returning' },
    dataset: 'Dataset',
    metricsLabel: 'Metrics (up to 4)',
    row1: 'Row dimension',
    row2: 'Second row dimension',
    column: 'Column dimension (pivot)',
    none: 'None',
    filtersTitle: 'Filters',
    warehouse: 'Warehouse',
    category: 'Category',
    brand: 'Brand',
    all: 'All',
    sort: 'Sort',
    sortAuto: 'Automatic',
    sortDesc: '{{metric}} (high to low)',
    sortAsc: '{{metric}} (low to high)',
    limit: 'Max rows',
    run: 'Run',
    resultTitle: 'Result',
    total: 'Total',
    empty: 'No data for this selection.',
    hidden: 'Empty because you cannot view costs: {{names}}',
    multiValued: 'Products in several categories count in each; rows may add up to more than the total.',
    truncatedRows:
      'Showing the first {{shown}} of {{total}} rows; the file has the same rows. You can raise the limit.',
    truncatedColumns: 'Showing the first 36 columns.',
    fxMissing: '{{count}} records without an exchange rate were left out.',
    pivotOneMetric: 'With a column dimension only the first metric is shown.',
    chartTitle: '{{metric}} — {{dim}}',
    savedTitle: 'Saved reports',
    savedEmpty: 'No saved reports.',
    sharedBy: 'Shared by {{name}}',
    mine: 'Mine',
    sharedMark: 'Shared',
    open: 'Open',
    current: 'Open report: {{name}}',
    newReport: 'New report',
    save: 'Save report',
    saveChanges: 'Save changes',
    saveAs: 'Save as new report',
    saveTitle: 'Save report',
    name: 'Report name',
    description: 'Description',
    visibility: 'Visibility',
    visibilities: { private: 'Only me', shared: 'Shared' },
    shareRoles: 'Share with roles',
    shareUsers: 'Share with people',
    shareNote:
      'People you share with see data with their own permissions: store scope and cost access apply.',
    saved: 'Report saved.',
    deleteReport: 'Delete report',
    deleteConfirm: 'The saved report will be deleted. People it was shared with will no longer see it.',
    deleted: 'Report deleted.',
    readOnly: 'This report was shared with you; you can save changes as a new report.',
    background: 'Prepare in background',
    backgroundQueued: 'The file is being prepared; download it from “My report files”.',
    openFiles: 'My report files',
    schedule: 'Schedule',
  },
  reportWidgets: {
    title: 'My report widgets',
    empty:
      'You have no report widgets. Open a report you saved or one shared with you in the report builder and use “Add to dashboard”.',
    goBuilder: 'Go to report builder',
    addToDashboard: 'Add to dashboard',
    added: 'Report added to the dashboard.',
    onDashboard: 'On dashboard',
    display: 'View',
    size: 'Width',
    displays: { table: 'Table', chart: 'Chart', number: 'Total only' },
    sizes: { half: 'Half', full: 'Full' },
    moveUp: 'Move up: {{name}}',
    moveDown: 'Move down: {{name}}',
    remove: 'Remove: {{name}}',
    removed: 'Widget removed.',
    unavailable: 'This report is no longer shared with you or was deleted.',
    unavailableTitle: 'Unavailable report',
    openReport: 'Open report',
    moreRows: '{{count}} more rows in the report',
    chartUnavailable: 'A chart needs a single row dimension and no column; showing the table.',
    settings: 'Widget settings: {{name}}',
  },
  reportFiles: {
    navLabel: 'My report files',
    filesTitle: 'My files',
    filesEmpty:
      'No files yet. Use “Prepare in background” in the report builder for a large report, or set up a scheduled report.',
    name: 'Report',
    format: 'Format',
    status: 'Status',
    rows: 'Rows',
    size: 'Size',
    created: 'Requested',
    expires: 'Deleted on',
    source: 'Source',
    sources: { manual: 'Request', schedule: 'Scheduled', workflow: 'Workflow' },
    statuses: { queued: 'Queued', running: 'Preparing', done: 'Ready', failed: 'Failed' },
    download: 'Download',
    downloadNamed: 'Download: {{name}}',
    failedGeneric: 'The file could not be produced; request it again.',
    keepNote:
      'Files are produced with your permissions, visible only to you and deleted after 30 days. Up to 50,000 rows.',
    kb: '{{n}} KB',
    schedulesTitle: 'Scheduled reports',
    schedulesEmpty: 'No scheduled reports.',
    newSchedule: 'New schedule',
    report: 'Saved report',
    pickReport: 'Choose a report',
    noSavedReports: 'Save a report in the report builder first.',
    frequency: 'Frequency',
    frequencies: { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' },
    weekday: 'Day',
    weekdays: {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday',
    },
    weekdaysInline: {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday',
    },
    monthDay: 'Day of month',
    hour: 'Hour (Türkiye)',
    when: {
      daily: 'Every day {{hour}}',
      weekly: 'Every {{day}} {{hour}}',
      monthly: 'Day {{day}} of every month {{hour}}',
    },
    periodNote: {
      daily: 'The previous day’s report is sent.',
      weekly: 'The previous week’s (Monday–Sunday) report is sent.',
      monthly: 'The previous month’s report is sent.',
    },
    recipients: 'Recipients',
    recipientCount: '{{count}} recipients',
    enabled: 'Sending on',
    enabledNamed: 'Sending: {{name}}',
    on: 'On',
    off: 'Off',
    nextRun: 'Next send',
    lastDelivery: 'Last send',
    never: 'Not yet',
    deliveryStatuses: { running: 'Sending', sent: 'Sent', partial: 'Partial', failed: 'Failed' },
    deliveryCounts: '{{sent}} sent · {{skipped}} skipped',
    edit: 'Edit',
    editNamed: 'Edit: {{name}}',
    deleteNamed: 'Delete: {{name}}',
    deleteConfirm: 'The schedule will be deleted. Files already produced stay until they expire.',
    deleted: 'Schedule deleted.',
    saved: 'Schedule saved.',
    dialogNew: 'Schedule report',
    dialogEdit: 'Edit schedule',
    deliveryNote:
      'The email contains no report data, only a link to this page. Each recipient’s file is produced with their own permissions; recipients who cannot see the report are skipped. A new schedule is off until you turn sending on.',
    unknownReport: 'Unavailable report',
  },
  disputes: {
    navLabel: 'Payment disputes',
    newDispute: 'New dispute',
    statusFilter: 'Status',
    filters: { active: 'Open', closed: 'Closed', all: 'All' },
    empty: 'No payment disputes recorded.',
    emptyActive: 'No open payment disputes.',
    tableTitle: 'Payment disputes',
    cols: {
      number: 'Dispute no',
      order: 'Order',
      kind: 'Type',
      status: 'Status',
      amount: 'Amount',
      fee: 'Fee',
      opened: 'Opened',
      due: 'Respond by',
      closed: 'Closed',
    },
    kinds: { chargeback: 'Chargeback', inquiry: 'Information request' },
    statuses: {
      open: 'Open',
      evidence_submitted: 'Evidence submitted',
      won: 'Won',
      lost: 'Lost',
      accepted: 'Accepted',
    },
    providers: { iyzico: 'iyzico', paytr: 'PayTR', manual: 'Physical POS / manual' },
    overdue: 'Overdue',
    dueSoon: 'Due soon',
    summary: '{{count}} open disputes. The amount at risk is shown in the finance report.',
    dialogTitle: 'Open a payment dispute',
    orderNumber: 'Order number',
    findPayments: 'Find payments',
    noPayments:
      'This order has no payment a dispute can be opened for. Disputes apply only to card or physical POS payments.',
    payment: 'Payment',
    paymentOption: '{{provider}} · {{amount}} · {{date}}',
    alreadyDisputed: 'Amount in dispute: {{amount}}',
    kind: 'Type',
    caseNumber: 'Bank/provider case number',
    reasonCode: 'Reason code',
    reason: 'Reason',
    amount: 'Disputed amount',
    fee: 'Dispute fee',
    openedOn: 'Notification date',
    responseDueOn: 'Respond by',
    note: 'Note',
    amountInvalid: 'The amount could not be read.',
    created: 'Dispute recorded.',
    saved: 'Dispute updated.',
    providerNote:
      'The record is kept in the panel; nothing is sent to the bank or payment provider. Submit your response and documents in the provider’s own panel.',
    detailTitle: 'Dispute {{number}}',
    back: 'Payment disputes',
    infoTitle: 'Details',
    order: 'Order',
    paymentInfo: 'Payment',
    paymentLine: '{{provider}} · {{amount}}',
    refunded: 'Refunded: {{amount}}',
    actionsTitle: 'Status',
    moveTo: {
      evidence_submitted: 'Mark evidence submitted',
      won: 'Won',
      lost: 'Lost',
      accepted: 'Accept (do not contest)',
      open: 'Reopen',
    },
    statusDialog: 'Change status: {{status}}',
    closedOn: 'Closing date',
    statusNote: 'Comment',
    reopenNote: 'A reason (e.g. arbitration) is required to reopen.',
    lossNote:
      'A lost or accepted amount is deducted from profit (the order’s period) and from cash (the closing date) in the finance report.',
    statusChanged: 'Status changed.',
    edit: 'Correct',
    editTitle: 'Correct dispute',
    documentsTitle: 'Documents',
    noDocuments: 'No documents added.',
    upload: 'Add document',
    uploadHint: 'PDF, JPEG or PNG; up to 10 MB. Documents are visible only in the panel.',
    uploaded: 'Document added.',
    downloadNamed: 'Download: {{name}}',
    notesTitle: 'Add note',
    addNote: 'Add note',
    noteAdded: 'Note added.',
    historyTitle: 'History',
    events: {
      created: 'Recorded',
      status: 'Status: {{from}} → {{to}}',
      updated: 'Details corrected',
      note: 'Note',
      document: 'Document added: {{name}}',
    },
    by: '{{name}}',
    system: 'System',
  },
  priceSimulation: {
    navLabel: 'Price simulation',
    priceList: 'Price list (TRY)',
    category: 'Category',
    brand: 'Brand',
    days: 'Sales volume period',
    lastDays: 'Last {{count}} days',
    priceChange: 'Price change (%)',
    costChange: 'Cost change (%)',
    feeRate: 'Fee rate (%)',
    feeDefault: 'empty: effective rate of the period',
    minMargin: 'Minimum margin (%)',
    percentInvalid: 'The percentage could not be read (e.g. 10, -5, 2.5).',
    run: 'Calculate',
    noWrite: 'Prices are not changed; this only calculates.',
    noLists: 'No TRY price lists.',
    summary: 'Simulation summary',
    currentContribution: 'Period contribution (current)',
    scenarioContribution: 'Period contribution (scenario)',
    periodOf: 'With sales volume of {{from}} – {{to}}',
    changeOf: 'Difference: {{amount}}',
    feeUsed: 'Fee in scenario',
    feeCurrent: 'Effective rate of the period {{pct}} ({{count}} payments)',
    feeNone: 'No payments in the period; effective rate taken as 0%.',
    warnings: 'Flagged products',
    warningsOf: '{{negative}} at a loss · {{low}} below minimum margin',
    assumption:
      'Contribution = price excl. VAT − cost − fee. Volume is assumed not to change with price; shipping and ads are not allocated per unit.',
    noCost: '{{count}} products without cost were left out.',
    feeUnknown: 'No fee rule for {{count}} card payments; the effective rate may be understated.',
    unitsHidden: 'Sales volume needs the sales report permission; period impact not calculated.',
    truncated: 'Showing the first {{count}} products (flagged first).',
    tableTitle: 'Unit economics by product (current → scenario)',
    empty: 'No products with a price and cost match the selection.',
    cols: {
      product: 'Product',
      units: 'Units sold',
      price: 'Price',
      net: 'Excl. VAT',
      cost: 'Cost',
      fee: 'Fee',
      contribution: 'Unit contribution',
      margin: 'Margin',
    },
    loss: 'Loss',
    low: 'Low margin',
    goBulk: 'Go to bulk price editing to apply prices',
  },
  marketingSummary: {
    navLabel: 'Marketing and SEO summary',
    funnelTitle: 'Storefront funnel (server records)',
    funnel: { carts: 'Added to cart', checkouts: 'Order step', orders: 'Confirmed orders' },
    stepRate: 'From previous step: {{pct}}',
    funnelOverall: 'Cart to confirmed order: {{pct}}.',
    funnelNote:
      'Product views are not stored on the server (they are in GA4, with consent). The order step includes pending and failed payments. One visitor may open several carts.',
    customersTitle: 'Customer acquisition and value',
    newCustomers: 'New customers',
    returning: 'Returning: {{count}}',
    cac: 'Ad cost per new customer (CAC)',
    cacOf: 'Ad spend: {{spend}}',
    cacHidden: 'Ad data is organization-wide; not shown with a store filter or without ads permission.',
    noAds: 'No ad account connected.',
    value12m: '12-month customer value',
    value12mOf: '{{customers}} customers in the last 12 months · {{orders}} orders per customer',
    valueToCac: 'Customer value ÷ CAC',
    valueToCacNote: 'Historical 12-month value; not a forecast.',
    adsTitle: 'Advertising',
    adSpend: 'Spend',
    clicks: 'Clicks',
    ctr: 'Click-through rate',
    cpc: 'Cost per click',
    openAds: 'Open the ads center',
    seoTitle: 'Organic search (Search Console)',
    seoNotLinked: 'Search Console is not linked.',
    organicClicks: 'Organic clicks',
    impressions: 'Impressions',
    position: 'Average position',
    seoLast: 'Latest data: {{date}} (Search Console data lags 2–3 days).',
    seoNoData: 'No synced data for this period.',
    messagingTitle: 'Email and message campaigns',
    channel: 'Channel',
    sent: 'Sent',
    delivered: 'Delivered',
    clicked: 'Clicked',
    email: 'Email',
    channels: { sms: 'SMS', whatsapp: 'WhatsApp' },
    sourcesTitle: 'Last-touch source of orders (storefront)',
    sourcesEmpty: 'No storefront orders in this period.',
    source: 'Source / medium',
    orders: 'Orders',
    share: 'Share',
    sourceKinds: { direct: 'Direct / unknown', no_consent: 'No cookie consent (source unknown)' },
    openAttribution: 'Open the sources report',
    fxMissing: '{{count}} foreign-currency orders without an exchange rate were left out of sales figures.',
  },
  multiTouch: {
    navLabel: 'Multi-touch attribution',
    dimension: 'Breakdown',
    dimensions: { source_medium: 'Source / medium', campaign: 'Campaign' },
    summary: 'Attribution summary',
    orders: 'Confirmed storefront orders',
    attributed: 'Orders with a known source',
    attributedOf: 'With cookie consent and at least one touch',
    withPath: 'Orders with middle touches',
    withPathOf: 'Middle touches are recorded since this feature went live',
    avgTouches: 'Average touches per order',
    tableTitle: 'Order share and net sales by model',
    models: { first: 'First touch', last: 'Last touch', linear: 'Linear', position: 'Position-based (U)' },
    orderShare: 'Orders',
    revenue: 'Net sales',
    kinds: { direct: 'Direct / no touch', no_consent: 'No cookie consent (source unknown)' },
    noCampaign: 'No campaign',
    empty: 'No confirmed storefront orders in this period.',
    note: 'Linear: equal share per touch. Position-based (U): 40% to first and last, 20% in total to middle touches (50/50 with two touches). Order shares and net sales (incl. VAT, after refunds and cancellations) are split without remainder.',
    pathNote:
      'Consecutive visits from the same source count as one touch; up to 5 middle touches are kept. Middle touches exist for orders placed after this feature; for earlier orders linear and U use first and last touch only.',
    fxMissing: '{{count}} foreign-currency orders without an exchange rate were left out.',
  },
  workflows: {
    navLabel: 'Automation flows',
    lead: 'Flows that act on their own when something happens (new product, paid order, critical stock, period end…): they check the conditions and run the actions. A flow handles events after it is turned on; actions run with the current permissions of the person who turned it on, and every step is logged. Messages to customers (cart reminders, back-in-stock notices) stay in their own sections with consent and IYS checks.',
    newFlow: 'New flow',
    newFlowName: 'New flow',
    pauseAll: 'Pause all',
    pauseAllConfirm: 'All active flows will be paused and pending runs cancelled. Are you sure?',
    pausedAll: '{{count}} flows paused.',
    autoPausedAlert: '{{count}} flows paused themselves; fix the cause and turn them on again:',
    flowsTitle: 'Flows',
    summaryLine: '{{active}} of {{total}} flows are on · {{failed}} failed runs in the last 7 days',
    flowsEmpty: 'No flows yet. Start with one of the ready-made examples below.',
    columns: {
      name: 'Flow',
      chain: 'Trigger → actions',
      status: 'Status',
      runs: 'Last 7 days',
      last: 'Last run',
    },
    runAsShort: 'Turned on by: {{name}}',
    runsCounts: '{{succeeded}} succeeded · {{failed}} failed · {{pending}} pending',
    never: 'Not run yet',
    next: 'Next run: {{date}}',
    statuses: { draft: 'Draft', active: 'On', paused: 'Paused', archived: 'Archived' },
    pausedReasons: {
      manual: 'Paused manually.',
      rate_limited: 'Paused itself because the hourly run limit was exceeded.',
      owner_inactive: 'Paused itself because the membership of the person who turned it on was closed.',
      owner_permission: 'Paused itself because the person who turned it on lacks a needed permission.',
    },
    templatesTitle: 'Ready-made examples',
    useTemplate: 'Start with this example',
    useTemplateNamed: 'Start with the example "{{name}}"',
    linked: 'Customer message — in its own section',
    linkedOn: 'On',
    linkedOff: 'Off',
    openLinked: 'Go to settings',
    templates: {
      new_product_seo_merchant: {
        name: 'New product → SEO check → Merchant Center',
        text: 'When a product is added, checks the SEO rules (title, description, image, barcode); if there are no issues it syncs the store products with Merchant Center, otherwise it stops and logs the issues.',
      },
      price_change_feed_log: {
        name: 'Price changed → Update feed → Change record',
        text: 'When a price changes, syncs the store products with Merchant Center and writes the event to the audit log. Runs at most every 15 minutes during frequent changes.',
      },
      critical_stock_notify: {
        name: 'Stock critical → Manager notice',
        text: 'Emails the team with stock permission when a variant falls to its critical threshold. Not repeated while it stays below; notified again if it is restocked and falls again.',
      },
      restock_customers: {
        name: 'Back in stock → Notify consenting customers',
        text: 'Customers who asked "notify me when available" are emailed when the product is back in stock (the request is the consent; the suppression list and queue apply). Managed in the Stock and price alerts section.',
      },
      paid_order_flow: {
        name: 'Order paid → Preparation → Invoice and shipping',
        text: 'Moves a paid order to "processing" (stock was reserved when the order was placed), opens a BizimHesap invoice draft and emails the team with shipping permission to prepare it.',
      },
      cart_reminder: {
        name: 'Cart abandoned → Consent check → Reminder',
        text: 'Sent by the "Abandoned cart" flow in Marketing automations: marketing consent, IYS, suppression list, frequency cap and quiet hours are checked before every email.',
      },
      approved_post_schedule: {
        name: 'New article approved → Scheduled publishing',
        text: 'Schedules an article approved by the expert for 10:00 the next day. Does not publish if the text changed after approval (the approval no longer applies).',
      },
      weekly_sales_report: {
        name: 'Week closed → Sales report → Email',
        text: 'Every Monday at 08:00 produces last week\'s saved report with each recipient\'s own permissions; the email contains only a link to "My report files".',
      },
      tls_expiring_alert: {
        name: 'SSL expiry approaching → Security alert',
        text: "Alerts store managers when a domain's SSL certificate has less than 14 days left or is faulty; once per certificate.",
      },
      payment_hook_failure: {
        name: 'POS notification error → Critical alert',
        text: 'Sends a critical alert to those with payment settings permission when a payment provider notification (webhook) or callback is rejected or cannot be processed; at most once an hour.',
      },
    },
    recentRuns: 'Recent runs',
    runsTitle: 'Run log',
    runsEmpty: 'No runs yet.',
    runColumns: { event: 'Event', status: 'Status', progress: 'Action', when: 'Time' },
    runStatuses: {
      pending: 'Pending',
      running: 'Running',
      succeeded: 'Succeeded',
      failed: 'Failed',
      skipped: 'Skipped',
      cancelled: 'Cancelled',
    },
    progress: '{{done}}/{{total}}',
    nextAttempt: 'Next attempt: {{date}}',
    back: 'Automation flows',
    builderLegend: 'Flow definition',
    name: 'Flow name',
    description: 'Description',
    runAs: "Turned on by: {{name}} — actions run with this person's current permissions.",
    activeEditNote:
      'The flow is on: your saved change applies to new events and the flow will run on your behalf from now on.',
    unsaved: 'There are unsaved changes.',
    problemsTitle: 'Fix before turning the flow on',
    problemAction: 'Action {{n}}',
    problemRule: 'Condition {{n}}',
    save: 'Save',
    saved: 'Flow saved.',
    activate: 'Turn on',
    activated: 'The flow is on; events from now on will be handled.',
    pause: 'Pause',
    paused: 'The flow was paused.',
    archive: 'Archive',
    archiveConfirm: 'The flow is archived and cannot be turned on again; the run log is kept. Are you sure?',
    archived: 'The flow was archived.',
    step1: 'Trigger',
    step2: 'Conditions',
    step3: 'Actions',
    trigger: 'When?',
    triggers: {
      product_created: 'A product was added',
      price_changed: 'A price changed',
      stock_critical: 'Stock fell to critical level',
      payment_captured: 'An order was paid',
      post_approved: 'An article got expert approval',
      schedule: 'A period closed (calendar)',
      tls_expiring: 'SSL certificate about to expire',
      payment_webhook_failed: 'Payment (POS) notification could not be processed',
      integration_error: 'A connection test failed',
    },
    triggerHints: {
      product_created: 'Every new product added in the panel, by import or by copying is an event.',
      price_changed:
        'A single price change, a bulk price operation (and its revert) and an exchange-rate update are events.',
      stock_critical:
        'Same as the critical stock rules in the stock report: sellable quantity at or below the threshold. A variant/warehouse newly entering the list is an event; those already critical when the flow is turned on are not counted.',
      payment_captured: 'Every payment captured by card, POS or "Payment received" is an event.',
      post_approved: 'An event when a veterinarian/expert approves a health article.',
      schedule:
        'Runs once at the day and time you choose (Turkey time) for the period that ended: yesterday, last week (Monday–Sunday) or last month.',
      tls_expiring:
        'A domain whose certificate is close to expiry or faulty according to the domain check; one event per certificate.',
      payment_webhook_failed:
        'An event when a notification or callback from the payment provider is rejected (invalid signature etc.) or cannot be processed.',
      integration_error: 'An event when a connection test in settings fails.',
    },
    frequencies: {
      daily: 'Daily (yesterday)',
      weekly: 'Weekly (last week)',
      monthly: 'Monthly (last month)',
    },
    weekdays: {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday',
    },
    noFields: 'This trigger has no condition fields; actions run on every event.',
    noConditions: 'No conditions: actions run on every event.',
    match: 'Conditions',
    matches: { all: 'All must hold', any: 'Any one is enough' },
    addRule: 'Add condition',
    ruleN: 'Condition {{n}}',
    removeRuleN: 'Remove condition {{n}}',
    ruleField: 'Field',
    ruleOp: 'Comparison',
    ruleValue: 'Value',
    ruleValueMoney: 'Amount (TRY)',
    ruleResult: { true: 'held', false: 'did not hold' },
    fields: {
      title: 'Name / title',
      status: 'Status',
      variantCount: 'Number of variants',
      brand: 'Brand',
      source: 'Type of change',
      priceList: 'Price list',
      sku: 'SKU',
      product: 'Product',
      available: 'Sellable quantity',
      threshold: 'Threshold',
      warehouse: 'Warehouse',
      scope: 'Threshold type',
      number: 'Order no',
      amount: 'Amount',
      currency: 'Currency',
      provider: 'Provider',
      channel: 'Channel',
      store: 'Store',
      published: 'Approved version is live',
      host: 'Domain',
      daysLeft: 'Days left',
      state: 'State',
      kind: 'Notification type',
      outcome: 'Outcome',
      message: 'Message',
      environment: 'Environment',
    },
    options: {
      draft: 'Draft',
      active: 'Active',
      archived: 'Archived',
      single: 'Single price',
      bulk: 'Bulk operation',
      revert: 'Bulk operation reverted',
      fx: 'Exchange-rate update',
      warehouse: 'Warehouse threshold',
      variant: 'Variant threshold',
      default: 'Default threshold',
      web: 'Storefront',
      b2b: 'Dealer',
      manual: 'Panel',
      api: 'API',
      expiring: 'Expiring',
      error: 'Faulty',
      webhook: 'Notification (webhook)',
      callback: 'Callback',
      rejected: 'Rejected',
      sandbox: 'Test',
      live: 'Live',
      yes: 'Yes',
      no: 'No',
    },
    ops: {
      eq: 'equals',
      neq: 'does not equal',
      gt: 'is greater than',
      gte: 'is at least',
      lt: 'is less than',
      lte: 'is at most',
      contains: 'contains',
      not_contains: 'does not contain',
      is_true: 'yes',
      is_false: 'no',
    },
    actions: {
      notify_staff: 'Email the team',
      seo_check: 'SEO check',
      merchant_sync: 'Sync with Merchant Center (feed)',
      record: 'Change record',
      order_processing: 'Move order to "processing"',
      invoice_draft: 'Invoice draft (BizimHesap)',
      publish_post: 'Publish or schedule the article',
      send_report: 'Send a saved report',
    },
    actionHints: {
      notify_staff:
        'Goes only to panel members (the team); no message is sent to customers. The email contains the flow name, the event and a link to the run log.',
      seo_check:
        'Checks the product with the SEO Command Center rules (even if not yet published). Issues are listed in the step log.',
      merchant_sync:
        'The selected store\'s products are sent to Merchant Center (unchanged ones are not resent). Without a Google connection the step is skipped as "setup required".',
      record: 'Writes the event and its fields to the audit log as an "automation record".',
      order_processing:
        'Only an order in "paid" status is moved to "processing"; if the team moved it already it is left alone. Stock was reserved when the order was placed and is deducted on shipping.',
      invoice_draft:
        'Opens a BizimHesap draft if the order has no invoice. If sending is on an e-Archive/e-Invoice is created; an uncertain or rejected send is not repeated automatically.',
      publish_post:
        'Publishes the approved article or schedules it for a later time. Does not publish if the text changed after approval.',
      send_report:
        'Produces the saved report for the period with each recipient\'s own permissions and puts it in "My report files"; the email contains no report data.',
    },
    actionN: 'Action {{n}}',
    addAction: 'Add action',
    moveUpN: 'Move action {{n}} up',
    moveDownN: 'Move action {{n}} down',
    removeActionN: 'Remove action {{n}}',
    notAllowed: '(you lack permission)',
    incompatible: '(not available for this trigger)',
    choose: 'Choose',
    params: {
      days: 'Days before expiry',
      frequency: 'Frequency',
      weekday: 'Day',
      monthDay: 'Day of month',
      hour: 'Time (Turkey)',
      recipients: 'To',
      permission: 'Permission',
      userIds: 'People',
      subject: 'Email subject',
      message: 'Message (optional)',
      stopOnIssues: 'Do not continue to the next actions if there are SEO issues',
      storeId: 'Store',
      note: 'Record note (optional)',
      send: 'Send the draft to BizimHesap right away (creates an e-Archive/e-Invoice)',
      mode: 'When',
      hours: 'Hours later',
      savedReportId: 'Saved report',
      format: 'File format',
      recipientIds: 'Recipients',
    },
    recipientsModes: { permission: 'Everyone with this permission', users: 'People I choose' },
    placeholders: 'Fields you can use in the text: {{list}}',
    publishModes: { now: 'Right away', delay: 'After some hours', next_day: 'Next day at a set time' },
    reportNone: 'No saved report; save a report in the report builder first.',
    advanced: 'Limits',
    throttle: 'Wait after a run (minutes)',
    throttleNote:
      '0 = none. Limits repeats for noisy events; events arriving in this window are logged as "skipped".',
    rate: 'Maximum runs per hour',
    rateNote: 'If exceeded the flow pauses itself and managers are emailed (loop and flood protection).',
    test: 'Try with recent events',
    testTitle: 'Test result',
    testNote:
      "The conditions were evaluated with the trigger's recent events; no action ran and nothing changed.",
    testEmpty: 'This trigger has no events yet.',
    matched: 'Conditions hold — actions would run',
    notMatched: 'Conditions do not hold — would be skipped',
    outOfScope: 'Outside your store scope — would be skipped',
    statusFilter: 'Status',
    runTitle: 'Run',
    triggerLabel: 'Trigger',
    event: 'Event',
    eventKey: 'Event key',
    createdAt: 'Event received',
    finishedAt: 'Finished',
    reasonLabel: 'Reason',
    lastError: 'Last error',
    attempts: 'Retry',
    fieldsTitle: 'Event fields',
    conditionsTitle: 'Conditions',
    stepsTitle: 'Step log',
    stepsEmpty: 'No action has run yet.',
    remaining: 'Actions not run: {{list}}',
    outcomes: { succeeded: 'Succeeded', skipped: 'Skipped', stopped: 'Stopped the flow', failed: 'Error' },
    attempt: 'Attempt {{n}}',
    reasons: {
      conditions_not_met: 'Conditions did not hold.',
      throttled: 'Arrived before the wait period ended.',
      store_scope: 'Outside the store scope of the person who turned the flow on.',
      workflow_paused: 'The flow was paused.',
      workflow_archived: 'The flow was archived.',
      manual: 'Cancelled manually.',
      manual_retry: 'Retried manually.',
      stopped: 'An action stopped the flow',
    },
    codes: {
      not_configured: 'Setup required: no or incomplete connection; step skipped.',
      internal_error: 'Temporary error; will be retried.',
      forbidden: 'The person who turned the flow on lacks permission for this.',
      unknown: 'Error code: {{code}}',
    },
    detail: {
      issues: 'SEO issues: {{list}}',
      recipients: 'Sent to {{count}} people.',
      sent: 'Sent to {{sent}} recipients, {{skipped}} skipped (no permission or membership closed).',
      publishAt: 'Publishing time: {{date}}',
      publishedNow: 'Published right away.',
      merchant: 'Merchant Center: {{inserted}} sent, {{deleted}} deleted, {{unchanged}} unchanged.',
      orderStatus: 'Order status: {{status}}',
      invoice: 'Invoice: {{status}}',
      permission: 'Permission needed: {{name}}',
    },
    invoiceStatuses: {
      draft: 'draft opened',
      sending: 'sending',
      sent: 'sent',
      failed: 'could not be sent',
      uncertain: 'result uncertain',
      cancelled: 'cancelled',
    },
    retry: 'Retry',
    retried: 'The run was queued again; it continues from the action where it stopped.',
    cancel: 'Cancel',
    cancelConfirm: 'The pending run is cancelled and its actions will not run.',
    cancelledToast: 'The run was cancelled.',
  },
} as const;
